import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { istYYMMDD } from '@/lib/ist'
import { newId } from '@/lib/ids'
import { enqueueCommands, nextDailyCounter, useDeviceInfo, type EnqueueInput } from '@/offline'
import { useToast } from '@/ui'
import {
  Btn,
  Cta,
  Footer,
  ItemRow,
  Keypad,
  Readout,
  Row,
  Strip,
  V1Screen,
  V1Sheet,
  WaitCta,
} from '@/features/gate/v1/kit'
import { C, typeKey } from '@/features/gate/v1/style'
import { fmtDate, kg, shortCode, shortLot, tr } from '@/features/gate/v1/text'
import type { DispatchShipmentPayload, LoadBagsPayload, ScanRefusal } from '../commands'
import type { BagView, StoreState } from '../projection'
import { checkLoadBag, isReportedRefusal, lotLoadCounts } from '../rules'
import { useDraftState } from '../useStore'
import {
  DoneScreen,
  Hint,
  IconBox,
  RefreshButton,
  RefusedCard,
  RefusedScreen,
  Saved,
  StoreHeader,
  Tally,
  Totals,
  VehicleRow,
  WithStore,
  supervisorPhone,
} from './bits'
import { SIcon } from './icons'
import { loadTotals, normVehicle, recentVehicles, type RecentVehicle } from './logic'
import { whyText } from './Receive'
import { TagScanner } from './TagScanner'

type Step = 'trip' | 'scan' | 'count' | 'send'

interface LoadDraft {
  step: Step
  shipment_id: string
  to_location_id: string | null
  vehicle_no: string
  bags: Array<{ container_id: string; loaded_at: string; item_id: string }>
  refused: Array<{ qr_payload: string; reason: ScanRefusal; scanned_at: string }>
  hand_count: string
}

const newDraft = (dest: string | null): LoadDraft => ({
  step: 'trip',
  shipment_id: newId(),
  to_location_id: dest,
  vehicle_no: '',
  bags: [],
  refused: [],
  hand_count: '',
})

/**
 * V1-F5 — गाड़ी लोड करें: where it goes and which vehicle, scan every bag on, count them by hand,
 * dispatch. V1 asks no driver, challan, bed check or photo (the server takes them as optional).
 */
export function V1Load() {
  const navigate = useNavigate()
  const home = () => navigate('/m')
  return <WithStore onBack={home}>{(state) => <LoadFlow state={state} onHome={home} />}</WithStore>
}

function LoadFlow({ state, onHome }: { state: StoreState; onHome: () => void }) {
  const actor = useActor()
  const toast = useToast()
  const device = useDeviceInfo()
  const from = state.siteId
  const destinations = state.cache.destinations.filter((d) => d.id !== from)
  const defaultDest =
    destinations.find((d) => d.default)?.id ??
    (destinations.length === 1 ? destinations[0]!.id : null)
  const [draft, update, loaded, clear] = useDraftState<LoadDraft>(
    `store.v1.load:${from}`,
    'store.v1.load',
    () => newDraft(defaultDest),
    actor.user_id,
  )
  const [remembered, remember, rememberedLoaded] = useDraftState<{ list: RecentVehicle[] }>(
    `store.v1.vehicles:${from}`,
    'store.v1.vehicles',
    () => ({ list: [] }),
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<{ bags: number; lots: number } | null>(null)

  const listed: BagView[] = useMemo(
    () => draft.bags.map((b) => state.bags.get(b.container_id)).filter((b): b is BagView => !!b),
    [draft.bags, state.bags],
  )
  const go = (step: Step) => update((d) => ({ ...d, step }))

  const dispatch = async () => {
    setSaving(true)
    try {
      const prefix = device.registration?.code_prefix ?? 'XX'
      const nn = await nextDailyCounter('shipment')
      const code = `MV-${istYYMMDD()}-${prefix}-${String(nn).padStart(2, '0')}`
      const partial = lotLoadCounts(state, from, listed)
        .filter((c) => c.left > 0)
        .map((c) => c.unit_id)
      const load: LoadBagsPayload = {
        shipment: {
          id: draft.shipment_id,
          code,
          kind: 'TRANSFER',
          from_location_id: from,
          to_location_id: draft.to_location_id!,
          vehicle_no: normVehicle(draft.vehicle_no),
        },
        items: draft.bags.map((b) => ({
          id: b.item_id,
          qr_payload: state.bags.get(b.container_id)!.qr_payload,
          container_id: b.container_id,
          loaded_at: b.loaded_at,
        })),
        refused_scans: draft.refused,
      }
      const send: DispatchShipmentPayload = {
        shipment_id: draft.shipment_id,
        hand_count: Number(draft.hand_count),
        partial_unit_ids: partial,
        dispatched_at: new Date().toISOString(),
      }
      const parents = [
        ...new Set(listed.map((b) => b.pending_command_id).filter((x): x is string => !!x)),
      ]
      const cmds: EnqueueInput[] = [
        { type: 'load_bags', payload: load, actorId: actor.user_id, parentIds: parents },
        { type: 'dispatch_shipment', payload: send, actorId: actor.user_id },
      ]
      await enqueueCommands(cmds)
      const totals = loadTotals(listed)
      remember((r) => ({
        list: recentVehicles([], from, [
          ...r.list,
          { vehicle_no: draft.vehicle_no, at: new Date().toISOString() },
        ]).slice(0, 8),
      }))
      setDone({ bags: totals.bags, lots: totals.lots })
      await clear()
    } catch (e) {
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (done)
    return (
      <DoneScreen
        title={tr('store.v1.dispatched.title')}
        lines={tr('store.v1.dispatched.line', done)}
        chips={[{ icon: 'scan', text: tr('store.v1.dispatched.rescan') }]}
        onHome={onHome}
      />
    )
  if (!loaded || !rememberedLoaded) return <V1Screen />
  const dest = destinations.find((d) => d.id === draft.to_location_id) ?? null

  if (draft.step === 'scan')
    return (
      <ScanOn
        state={state}
        draft={draft}
        listed={listed}
        destName={dest?.name ?? ''}
        update={update}
        onBack={() => go('trip')}
        onDone={() => go('count')}
      />
    )
  if (draft.step === 'count')
    return (
      <HandCount
        value={draft.hand_count}
        onChange={(v) => update((d) => ({ ...d, hand_count: v }))}
        onBack={() => go('scan')}
        onNext={() => go('send')}
      />
    )
  if (draft.step === 'send') {
    const totals = loadTotals(listed)
    const counted = Number(draft.hand_count)
    const off = counted !== totals.bags
    return (
      <V1Screen
        header={
          <StoreHeader
            onBack={() => go('count')}
            icon="truck"
            title={tr('store.v1.dispatch.title')}
            step={2}
          />
        }
        footer={
          <Footer>
            {off ? (
              <WaitCta onClick={() => go('count')}>
                {tr('store.v1.dispatch.countOff', { count: counted, scanned: totals.bags })}
              </WaitCta>
            ) : (
              <Cta
                label={tr('store.v1.dispatch.yes')}
                icon="truck"
                busy={saving}
                onClick={() => void dispatch()}
              />
            )}
          </Footer>
        }
      >
        <VehicleRow
          vehicle={normVehicle(draft.vehicle_no)}
          line={tr('store.v1.dispatch.going', { place: dest?.name ?? '' })}
        />
        <Totals
          rows={[
            [tr('store.v1.lots'), String(totals.lots)],
            [tr('store.v1.bags'), String(totals.bags)],
            [tr('store.v1.kg'), kg(totals.netG)],
          ]}
        />
        <ItemRow
          lead={<SIcon name="keypad" size={26} />}
          title={tr('store.v1.dispatch.handCount')}
          right={<b style={{ fontSize: 24, color: off ? C.bad : C.ink }}>{counted}</b>}
        />
        <Hint icon="lock" text={tr('store.v1.dispatch.locked')} />
      </V1Screen>
    )
  }

  return (
    <TripStep
      destinations={destinations}
      draft={draft}
      vehicles={recentVehicles(state.trips.values(), from, remembered.list)}
      update={update}
      onBack={onHome}
      onNext={() => go('scan')}
    />
  )
}

// ---------------------------------------------------------------------------------------------
// Where and which vehicle
// ---------------------------------------------------------------------------------------------

function TripStep({
  destinations,
  draft,
  vehicles,
  update,
  onBack,
  onNext,
}: {
  destinations: StoreState['cache']['destinations']
  draft: LoadDraft
  vehicles: RecentVehicle[]
  update: (fn: (d: LoadDraft) => LoadDraft) => void
  onBack: () => void
  onNext: () => void
}) {
  const [picking, setPicking] = useState(!draft.to_location_id)
  const [typing, setTyping] = useState(false)
  const dest = destinations.find((d) => d.id === draft.to_location_id) ?? null
  const typed = normVehicle(draft.vehicle_no)
  const all =
    typed && !vehicles.some((v) => v.vehicle_no === typed)
      ? [{ vehicle_no: typed, at: null }, ...vehicles]
      : vehicles

  return (
    <V1Screen
      header={
        <StoreHeader onBack={onBack} icon="truck" title={tr('store.v1.load.title')} step={1} />
      }
      footer={
        <Footer>
          {!dest ? (
            <WaitCta>{tr('store.v1.load.pickDest')}</WaitCta>
          ) : !typed ? (
            <WaitCta onClick={() => setTyping(true)}>{tr('store.v1.load.pickVehicle')}</WaitCta>
          ) : (
            <Cta label={tr('gate.v1.next')} icon="next" onClick={onNext} />
          )}
        </Footer>
      }
      overlay={
        typing ? (
          <VehicleSheet
            initial={draft.vehicle_no}
            onClose={() => setTyping(false)}
            onSave={(v) => {
              update((d) => ({ ...d, vehicle_no: v }))
              setTyping(false)
            }}
          />
        ) : undefined
      }
    >
      <b style={{ fontSize: 18 }}>{tr('store.v1.load.where')}</b>
      {destinations.length === 0 ? (
        <>
          <Strip icon="bang">{tr('store.v1.load.noDest')}</Strip>
          <Row>
            <RefreshButton />
          </Row>
        </>
      ) : dest && !picking ? (
        <ItemRow
          padding="8px 10px"
          lead={<IconBox icon="godown" size={44} iconSize={26} />}
          title={dest.name}
          right={
            destinations.length > 1 ? (
              <div style={{ flex: '0 0 auto', display: 'flex' }}>
                <Btn onClick={() => setPicking(true)}>{tr('store.v1.load.change')}</Btn>
              </div>
            ) : undefined
          }
        />
      ) : (
        destinations.map((d) => (
          <ItemRow
            key={d.id}
            padding="8px 10px"
            onClick={() => {
              update((x) => ({ ...x, to_location_id: d.id }))
              setPicking(false)
            }}
            border={d.id === draft.to_location_id ? C.ink : C.border}
            borderWidth={d.id === draft.to_location_id ? 3 : 1.5}
            lead={<IconBox icon="godown" size={44} iconSize={26} />}
            title={d.name}
          />
        ))
      )}
      <b style={{ fontSize: 18 }}>{tr('store.v1.load.which')}</b>
      <Hint icon="eye" text={tr('store.v1.load.plate')} />
      {all.map((v) => (
        <VehicleRow
          key={v.vehicle_no}
          box={44}
          vehicle={v.vehicle_no}
          quiet
          line={v.at ? tr('store.v1.load.lastTime', { date: fmtDate(v.at) }) : undefined}
          picked={v.vehicle_no === typed}
          onClick={() => update((d) => ({ ...d, vehicle_no: v.vehicle_no }))}
        />
      ))}
      <Row>
        <Btn icon="pencil" onClick={() => setTyping(true)}>
          {tr('store.v1.load.newVehicle')}
        </Btn>
      </Row>
    </V1Screen>
  )
}

/** नई गाड़ी: the number plate as written (letters and digits). */
function VehicleSheet({
  initial,
  onClose,
  onSave,
}: {
  initial: string
  onClose: () => void
  onSave: (v: string) => void
}) {
  const [v, setV] = useState(initial)
  const ok = normVehicle(v).replace(/\s/g, '').length >= 4
  return (
    <V1Sheet
      title={tr('store.v1.load.typeTitle')}
      onClose={onClose}
      top={200}
      actions={
        ok ? (
          <Cta label={tr('gate.v1.next')} icon="next" onClick={() => onSave(normVehicle(v))} />
        ) : (
          <WaitCta>{tr('store.v1.load.typeTitle')}</WaitCta>
        )
      }
    >
      <span style={{ fontSize: 16, color: C.muted }}>{tr('store.v1.load.typeHint')}</span>
      <input
        autoFocus
        value={v}
        maxLength={20}
        autoCapitalize="characters"
        autoComplete="off"
        aria-label={tr('store.v1.load.typeTitle')}
        onChange={(e) => setV(e.target.value.toUpperCase())}
        onKeyDown={(e) => e.key === 'Enter' && ok && onSave(normVehicle(v))}
        style={{
          fontSize: 30,
          fontWeight: 700,
          padding: '8px 12px',
          borderRadius: 12,
          border: `2px solid ${C.acc}`,
          background: C.surface,
          color: C.ink,
          letterSpacing: 1,
          width: '100%',
          boxSizing: 'border-box',
        }}
      />
    </V1Sheet>
  )
}

// ---------------------------------------------------------------------------------------------
// Scan bags on
// ---------------------------------------------------------------------------------------------

function ScanOn({
  state,
  draft,
  listed,
  destName,
  update,
  onBack,
  onDone,
}: {
  state: StoreState
  draft: LoadDraft
  listed: BagView[]
  destName: string
  update: (fn: (d: LoadDraft) => LoadDraft) => void
  onBack: () => void
  onDone: () => void
}) {
  const [last, setLast] = useState<string | null>(null)
  const [refused, setRefused] = useState<{ tag: string; reason: ScanRefusal } | null>(null)
  const [fixLast, setFixLast] = useState(false)
  const [removing, setRemoving] = useState(false)
  const from = state.siteId
  // V1 has no bed check: every bag may go (load_bags leaves vehicle_clean out).
  const ctx = {
    fromLocationId: from,
    vehicleClean: true,
    onList: new Set(draft.bags.map((b) => b.container_id)),
    samplesOnList: new Set<string>(),
  }
  const here = useMemo(
    () =>
      [...state.bags.values()]
        .filter((b) => !b.shipment_id && (b.status === 'SEALED' || b.status === 'STORED'))
        .map((b) => b.qr_payload),
    [state.bags],
  )
  const counts = lotLoadCounts(state, from, listed)
  const full = counts.filter((c) => c.left === 0).length

  const onCode = (code: string) => {
    const v = checkLoadBag(state, code, ctx)
    if (!v.ok) {
      if (v.reason === 'DUPLICATE') return null
      const reason = v.reason
      if (isReportedRefusal(reason))
        update((d) =>
          d.refused.some((r) => r.qr_payload === code)
            ? d
            : {
                ...d,
                refused: [
                  ...d.refused,
                  { qr_payload: code, reason, scanned_at: new Date().toISOString() },
                ],
              },
        )
      setRefused({ tag: code, reason })
      return null
    }
    update((d) => ({
      ...d,
      bags: [
        ...d.bags,
        {
          container_id: v.value.container_id,
          loaded_at: new Date().toISOString(),
          item_id: newId(),
        },
      ],
    }))
    setLast(v.value.qr_payload)
    return null
  }

  const removeCode = (code: string) => {
    const bag = state.bagByPayload.get(code)
    if (!bag || !draft.bags.some((b) => b.container_id === bag.container_id))
      return tr('store.v1.remove.notOn')
    update((d) => ({ ...d, bags: d.bags.filter((b) => b.container_id !== bag.container_id) }))
    setLast(null)
    return null
  }

  if (refused)
    return (
      <RefusedScreen
        icon="truck"
        title={tr('store.v1.scanOn.title')}
        step={2}
        phone={supervisorPhone(state)}
        onBack={() => setRefused(null)}
        onOk={() => setRefused(null)}
        card={
          <RefusedCard
            title={tr('store.v1.refused.load', { tag: shortCode(refused.tag) })}
            why={whyText(refused.reason)}
            steps={[tr('store.v1.refused.loadStep1'), tr('store.v1.refused.loadStep2')]}
          />
        }
      />
    )

  const lastBag = last ? state.bagByPayload.get(last) : undefined
  return (
    <V1Screen
      header={
        <StoreHeader onBack={onBack} icon="truck" title={tr('store.v1.scanOn.title')} step={2} />
      }
      footer={
        <Footer>
          {draft.bags.length > 0 ? (
            <Cta label={tr('store.v1.scanOn.done')} icon="next" onClick={onDone} />
          ) : (
            <WaitCta>{tr('store.v1.scanOn.first')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        fixLast && lastBag ? (
          <V1Sheet
            title={tr('store.v1.fixLast.title')}
            onClose={() => setFixLast(false)}
            top={380}
            actions={
              <Cta
                label={tr('store.v1.fixLast.yes')}
                icon="undo"
                lead
                onClick={() => {
                  removeCode(lastBag.qr_payload)
                  setFixLast(false)
                }}
              />
            }
          >
            <span style={{ fontSize: 18 }}>{tr('store.v1.fixLast.body')}</span>
            <b style={{ fontSize: 22 }}>
              {tr('store.v1.tag', { tag: shortCode(lastBag.qr_payload) })}
            </b>
          </V1Sheet>
        ) : removing ? (
          <RemoveSheet
            payloads={listed.map((b) => b.qr_payload)}
            onCode={removeCode}
            onClose={() => setRemoving(false)}
          />
        ) : undefined
      }
    >
      {last && (
        <button
          type="button"
          onClick={() => setFixLast(true)}
          style={{ border: 0, padding: 0, background: 'none', textAlign: 'left' }}
        >
          <Saved text={tr('store.v1.tag', { tag: shortCode(last) })} />
        </button>
      )}
      <VehicleRow
        vehicle={normVehicle(draft.vehicle_no)}
        line={destName}
        right={<Tally value={String(draft.bags.length)} label={tr('store.v1.scanOn.on')} />}
      />
      <TagScanner
        hint={tr(draft.bags.length ? 'store.v1.scanOn.hold' : 'store.v1.scanOn.first')}
        payloads={here}
        onCode={onCode}
        paused={fixLast || removing}
        side={
          draft.bags.length
            ? {
                icon: 'undo',
                label: tr('store.v1.scanOn.remove'),
                onClick: () => setRemoving(true),
              }
            : undefined
        }
      />
      {counts.length > 0 && (
        <>
          <b style={{ fontSize: 17 }}>{tr('store.v1.scanOn.lotsTitle')}</b>
          <span style={{ fontSize: 16, color: C.muted }}>
            {tr('store.v1.scanOn.lotsLine', { full, left: counts.length - full })}
          </span>
          {counts.slice(0, 4).map((c) => {
            const lot = state.lots.get(c.unit_id)
            const red = lot?.stream === 'CONV'
            return (
              <ItemRow
                key={c.unit_id}
                padding="6px 10px"
                lead={
                  <span style={{ color: red ? C.ink : C.ok, display: 'flex' }}>
                    <SIcon name={red ? 'redtie' : 'sack'} size={26} />
                  </span>
                }
                title={lot ? shortLot(lot.code) : '—'}
                right={<Tally value={`${c.on}/${c.total}`} label={tr('store.v1.scanOn.on')} />}
              />
            )
          })}
          {counts.length > 4 && (
            <span style={{ fontSize: 16, color: C.muted }}>
              {tr('store.v1.scanOn.moreLots', { count: counts.length - 4 })}
            </span>
          )}
        </>
      )}
    </V1Screen>
  )
}

/** बोरी हटाएँ: scan (or type) the tag of each bag to take off the list. */
function RemoveSheet({
  payloads,
  onCode,
  onClose,
}: {
  payloads: string[]
  onCode: (code: string) => string | null
  onClose: () => void
}) {
  const [removed, setRemoved] = useState<string | null>(null)
  return (
    <V1Sheet
      title={tr('store.v1.scanOn.remove')}
      onClose={onClose}
      top={120}
      actions={<Cta label={tr('store.v1.remove.done')} icon="ok" onClick={onClose} />}
    >
      {removed && <Saved text={tr('store.v1.remove.removed', { tag: shortCode(removed) })} />}
      <TagScanner
        hint={tr('store.v1.remove.hint')}
        payloads={payloads}
        onCode={(code) => {
          const e = onCode(code)
          if (!e) setRemoved(code)
          return e
        }}
      />
    </V1Sheet>
  )
}

// ---------------------------------------------------------------------------------------------
// Count by hand
// ---------------------------------------------------------------------------------------------

function HandCount({
  value,
  onChange,
  onBack,
  onNext,
}: {
  value: string
  onChange: (v: string) => void
  onBack: () => void
  onNext: () => void
}) {
  const onKey = useCallback(
    (k: string) => onChange(typeKey(value, k, { decimals: 0, max: 4 })),
    [value, onChange],
  )
  return (
    <V1Screen
      header={
        <StoreHeader onBack={onBack} icon="truck" title={tr('store.v1.count.title')} step={2} />
      }
      footer={
        <Footer>
          {value ? (
            <Cta label={tr('gate.v1.next')} icon="next" onClick={onNext} />
          ) : (
            <WaitCta>{tr('store.v1.count.type')}</WaitCta>
          )}
        </Footer>
      }
    >
      <Readout label={tr('store.v1.count.label')} value={value} />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Screen>
  )
}
